import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getCostBasis, CostBasisItem, CostBasisParams } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatUsd, formatCrypto, formatDate, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';

// Server-paginated. See CoinbaseTransactionsView.tsx for the canonical idiom.

type Method = 'SPECIFIC_ID' | 'HIFO' | 'LIFO' | 'FIFO' | 'ZERO_BASIS_UNKNOWN';
type HoldingPeriod = 'SHORT_TERM' | 'LONG_TERM';

const METHOD_OPTIONS = [
  { value: 'SPECIFIC_ID', label: 'SPECIFIC ID' },
  { value: 'HIFO', label: 'HIFO' },
  { value: 'LIFO', label: 'LIFO' },
  { value: 'FIFO', label: 'FIFO' },
  { value: 'ZERO_BASIS_UNKNOWN', label: 'ZERO BASIS UNKNOWN' },
];

const HOLDING_OPTIONS = [
  { value: 'SHORT_TERM', label: 'Short Term' },
  { value: 'LONG_TERM', label: 'Long Term' },
];

const renderGainLoss = (raw: string) => {
  const num = parseFloat(raw);
  if (Number.isNaN(num)) return formatUsd(raw);
  const cls = num < 0 ? 'pnl-negative' : num > 0 ? 'pnl-positive' : '';
  return <span className={cls}>{formatUsd(raw)}</span>;
};

export const CostBasisView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [portfolioId, setPortfolioId] = useState('');
  const [productId, setProductId] = useState('');
  const [method, setMethod] = useState<'' | Method>('');
  const [holdingPeriod, setHoldingPeriod] = useState<'' | HoldingPeriod>('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'disposal_date', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;


  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params: CostBasisParams = {
    taxYear,
    portfolioId: portfolioId || undefined,
    productId: productId || undefined,
    method: (method || undefined) as Method | undefined,
    holdingPeriod: (holdingPeriod || undefined) as HoldingPeriod | undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'cost-basis', taxYear, portfolioId, productId, method, holdingPeriod, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getCostBasis(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const { options: portfolioOptions } = usePortfolioRegistry();
  const productOptions = useMemo(
    () => Array.from(new Set(items.map((i) => i.product_id))).sort().map((p) => ({ value: p, label: p })),
    [items]
  );

  // Column order: acquisition (buy) columns before disposal (sell) columns —
  // matches real-world transaction order. Default sort is still disposal_date desc.
  const columns = useMemo<ColumnDef<CostBasisItem>[]>(
    () => [
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },

      // Acquisition (buy) side
      { accessorKey: 'acquisition_type', header: 'Acq Type' },
      {
        accessorKey: 'acquisition_id',
        header: 'Acq ID',
        cell: (info) => { const v = info.getValue() as string | null; return <span className="order-id-cell">{formatOrderId(v)}</span>; },
        enableSorting: false,
      },
      {
        accessorKey: 'acquisition_date',
        header: 'Acq Date',
        cell: (info) => <span className="date-cell">{formatDate(info.getValue() as string | null)}</span>,
      },
      { accessorKey: 'acquisition_cost', header: 'Acq Cost', cell: (info) => formatUsd(info.getValue() as string) },

      // Disposal (sell) side
      {
        accessorKey: 'disposal_date',
        header: 'Disposal Date',
        cell: (info) => <span className="date-cell">{formatDate(info.getValue() as string)}</span>,
      },
      {
        accessorKey: 'disposal_order_id',
        header: 'Disposal Order',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
      { accessorKey: 'disposal_quantity', header: 'Quantity', cell: (info) => formatCrypto(info.getValue() as string) },
      { accessorKey: 'disposal_proceeds', header: 'Proceeds', cell: (info) => formatUsd(info.getValue() as string) },

      // Outcome
      { accessorKey: 'gain_loss', header: 'Gain/Loss', cell: (info) => renderGainLoss(info.getValue() as string) },
      { accessorKey: 'holding_period', header: 'Holding' },
      { accessorKey: 'method', header: 'Method' },
    ],
    []
  );

  const filters: DataTableFilter[] = [
    { id: 'portfolio_id', label: 'Portfolio', value: portfolioId, options: portfolioOptions, onChange: onFilterChange(setPortfolioId) },
    { id: 'product_id', label: 'Product', value: productId, options: productOptions, onChange: onFilterChange(setProductId) },
    {
      id: 'method',
      label: 'Method',
      value: method,
      options: METHOD_OPTIONS,
      onChange: (v) => {
        setMethod(v as '' | Method);
        setPageIndex(0);
      },
    },
    {
      id: 'holding_period',
      label: 'Holding',
      value: holdingPeriod,
      options: HOLDING_OPTIONS,
      onChange: (v) => {
        setHoldingPeriod(v as '' | HoldingPeriod);
        setPageIndex(0);
      },
    },
  ];

  return (
    <div aria-label="Cost Basis view">
      <DataTable<CostBasisItem>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading cost basis…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load cost basis results'}
        emptyMessage="No cost basis results for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default CostBasisView;
